import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, FolderKanban, Users, UserCog, LogOut, Building2, ReceiptText, Files } from "lucide-react";
import { Settings } from "lucide-react";
import { useStudio } from "@/context/StudioContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link", end: true },
  { to: "/pratiche", label: "Pratiche", icon: FolderKanban, testid: "nav-pratiche-link", permission: "pratiche" },
  { to: "/clienti", label: "Clienti", icon: Users, testid: "nav-clienti-link", permission: "clienti" },
  { to: "/preventivi", label: "Preventivi", icon: ReceiptText, testid: "nav-preventivi-link", permission: "preventivi" },
  { to: "/modulistica", label: "Modulistica", icon: Files, testid: "nav-modulistica-link", permission: "modulistica" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { settings } = useStudio();
  const navigate = useNavigate();

  const items = NAV.filter((item) => user?.role === "admin" || !item.permission || user?.permissions?.includes(item.permission));
  if (user?.role === "admin") {
    items.push({ to: "/collaboratori", label: "Collaboratori", icon: UserCog, testid: "nav-collaboratori-link" });
    items.push({ to: "/impostazioni", label: "Impostazioni", icon: Settings, testid: "nav-impostazioni-link" });
  }

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
      isActive ? "bg-blue-800" : "opacity-75 hover:bg-slate-800 hover:opacity-100"
    }`;

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside style={{backgroundColor:"var(--sidebar-color, #0f172a)",color:"var(--sidebar-text-color, #ffffff)"}} className="print-hidden hidden md:flex md:w-64 flex-col fixed inset-y-0 left-0 z-30" data-testid="sidebar">
        <div className="flex items-center gap-3 px-6 py-6 border-b border-slate-800">
          <div className="h-10 w-10 rounded-lg bg-blue-800 flex items-center justify-center overflow-hidden">
            {settings?.logo_url ? <img src={settings.logo_url} alt="Logo" className="h-full w-full object-contain bg-white" /> : <Building2 className="h-5 w-5 text-white" />}
          </div>
          <div>
            <p className="font-heading font-extrabold tracking-tight text-lg leading-none truncate max-w-36">{settings?.studio_name || "CLGGEST"}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest opacity-75 mt-1">Pro · Studio Tecnico</p>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {items.map(({ to, label, icon: Icon, testid, end }) => (
            <NavLink key={to} to={to} end={end} className={linkCls} data-testid={testid}>
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-slate-800">
          <p className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</p>
          <p className="text-xs opacity-60 truncate">{user?.email}</p>
          <p className="font-mono text-[10px] uppercase tracking-wider opacity-80 mt-1">
            {user?.role === "admin" ? "Titolare" : "Collaboratore"}
          </p>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="mt-3 flex items-center gap-2 text-xs opacity-60 hover:opacity-100 transition-opacity"
          >
            <LogOut className="h-3.5 w-3.5" /> Esci
          </button>
        </div>
      </aside>

      <div style={{backgroundColor:"var(--sidebar-color, #0f172a)",color:"var(--sidebar-text-color, #ffffff)"}} className="print-hidden md:hidden fixed top-0 inset-x-0 z-30" data-testid="mobile-nav">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            <span className="font-heading font-extrabold tracking-tight">CLGGEST</span>
          </div>
          <button onClick={handleLogout} data-testid="logout-button-mobile" className="opacity-75">
            <LogOut className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex max-w-full overflow-x-auto overscroll-x-contain px-2 pb-2 gap-1 touch-pan-x">
          {items.map(({ to, label, icon: Icon, testid, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`${testid}-mobile`}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                  isActive ? "bg-blue-800" : "opacity-75 bg-slate-800"
                }`
              }
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </NavLink>
          ))}
        </nav>
      </div>

      <main className="flex-1 min-w-0 w-full md:ml-64 pt-28 md:pt-0">
        <div className="w-full min-w-0 p-3 sm:p-6 lg:p-8 max-w-7xl mx-auto fade-in">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
